import { v4 as uuidV4 } from "uuid";

import type { RunMatcherResult } from "../matching";
import {
  findFileLocations,
  getBlockingErrorsForPhase,
} from "./selectors";
import type {
  FileLocationRef,
  Group,
  WizardData,
  WizardPhase,
} from "./types";

export type WizardState = {
  phase: WizardPhase;
  data: WizardData;
};

export type WizardAction =
  | { type: "SET_PERIOD"; period_date: string }
  | { type: "MATCH_FILES"; result: RunMatcherResult }
  | { type: "APPROVE_GROUP"; group_id: string }
  | { type: "MARK_NEEDS_EDIT"; group_id: string }
  | { type: "SELECT_CUSTOMER"; group_id: string; customer_id: string | null }
  | { type: "CONTINUE_TO_MANUAL" }
  | {
      type: "MOVE_FILE";
      file_id: string;
      source: FileLocationRef;
      target: FileLocationRef;
    }
  | { type: "CREATE_MANUAL_GROUP"; file_ids: string[] }
  | { type: "DISSOLVE_GROUP"; group_id: string }
  | { type: "CONTINUE_TO_FINAL" }
  | { type: "BACK_TO_MANUAL" }
  | { type: "START_UPLOAD" }
  | { type: "RESET" };

export function makeInitialWizardState(): WizardState {
  return {
    phase: "select",
    data: {
      period_date: "",
      files: {},
      groups: [],
      ungrouped_file_ids: [],
    },
  };
}

// ---------------------------------------------------------------------------
// Internal mechanics (validation is delegated to selectors)
// ---------------------------------------------------------------------------

function findGroupIndex(data: WizardData, groupId: string): number {
  return data.groups.findIndex((g: Group) => g.id === groupId);
}

function replaceGroup(
  data: WizardData,
  groupId: string,
  replacer: (group: Group) => Group,
): WizardData {
  const index = findGroupIndex(data, groupId);
  if (index === -1) return data;
  const next = data.groups.slice();
  next[index] = replacer(next[index]);
  return { ...data, groups: next };
}

/**
 * `CONTINUE_TO_MANUAL` only requires that no suggested group is still in
 * the `suggested` state. This is stricter-scoped than the
 * `review_suggested` blocking-error selector (which also reports file
 * accounting errors), so it stays as a tiny inline check.
 */
function hasUnresolvedSuggestedGroup(data: WizardData): boolean {
  return data.groups.some((g: Group) => g.state === "suggested");
}

function locationsEqual(a: FileLocationRef, b: FileLocationRef): boolean {
  if (a.kind === "ungrouped" && b.kind === "ungrouped") return true;
  if (a.kind === "group" && b.kind === "group") return a.group_id === b.group_id;
  return false;
}

/**
 * In Phase 3, approved groups are read-only — they cannot be sources or
 * targets of MOVE_FILE, cannot be dissolved, and their selected customer
 * cannot be changed.
 */
function isLocationEditableInManualPhase(
  data: WizardData,
  location: FileLocationRef,
): boolean {
  if (location.kind === "ungrouped") return true;
  const group = data.groups.find((g: Group) => g.id === location.group_id);
  if (!group) return false;
  return group.state !== "approved";
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

/**
 * Wizard reducer. Guard violations silently return the same state — the
 * UI uses selectors to disable invalid actions, so throwing here would
 * make the UI brittle.
 *
 * No file ever disappears from `groups + ungrouped_file_ids` as a result
 * of any action.
 */
export function wizardReducer(
  state: WizardState,
  action: WizardAction,
): WizardState {
  switch (action.type) {
    case "RESET": {
      return makeInitialWizardState();
    }

    case "SET_PERIOD": {
      if (state.phase !== "select") return state;
      return {
        ...state,
        data: { ...state.data, period_date: action.period_date },
      };
    }

    case "MATCH_FILES": {
      if (state.phase !== "select") return state;
      if (state.data.period_date === "") return state;
      const fileIds = Object.keys(action.result.files);
      if (fileIds.length === 0) return state;
      return {
        phase: "review_suggested",
        data: {
          period_date: state.data.period_date,
          files: action.result.files,
          groups: action.result.groups,
          ungrouped_file_ids: action.result.ungrouped_file_ids,
        },
      };
    }

    case "APPROVE_GROUP": {
      if (state.phase !== "review_suggested") return state;
      const group = state.data.groups.find(
        (g: Group) => g.id === action.group_id,
      );
      if (!group) return state;
      if (group.selected_customer_id === null) return state;
      return {
        ...state,
        data: replaceGroup(state.data, action.group_id, (g: Group) => ({
          ...g,
          state: "approved",
        })),
      };
    }

    case "MARK_NEEDS_EDIT": {
      if (state.phase !== "review_suggested") return state;
      const group = state.data.groups.find(
        (g: Group) => g.id === action.group_id,
      );
      if (!group) return state;
      return {
        ...state,
        data: replaceGroup(state.data, action.group_id, (g: Group) => ({
          ...g,
          state: "needs_edit",
        })),
      };
    }

    case "SELECT_CUSTOMER": {
      if (
        state.phase !== "review_suggested" &&
        state.phase !== "manual_grouping"
      ) {
        return state;
      }
      const group = state.data.groups.find(
        (g: Group) => g.id === action.group_id,
      );
      if (!group) return state;
      // In Phase 3, approved groups are read-only.
      if (
        state.phase === "manual_grouping" &&
        !isLocationEditableInManualPhase(state.data, {
          kind: "group",
          group_id: action.group_id,
        })
      ) {
        return state;
      }
      return {
        ...state,
        data: replaceGroup(state.data, action.group_id, (g: Group) => ({
          ...g,
          selected_customer_id: action.customer_id,
        })),
      };
    }

    case "CONTINUE_TO_MANUAL": {
      if (state.phase !== "review_suggested") return state;
      if (hasUnresolvedSuggestedGroup(state.data)) return state;
      return { ...state, phase: "manual_grouping" };
    }

    case "MOVE_FILE": {
      if (state.phase !== "manual_grouping") return state;
      if (locationsEqual(action.source, action.target)) return state;
      // Source/target may not be an approved group.
      if (!isLocationEditableInManualPhase(state.data, action.source)) {
        return state;
      }
      if (!isLocationEditableInManualPhase(state.data, action.target)) {
        return state;
      }
      // Verify the file is actually at the declared source.
      const actualLocations = findFileLocations(state.data, action.file_id);
      if (actualLocations.length !== 1) return state;
      if (!locationsEqual(actualLocations[0], action.source)) return state;
      // Verify the target exists if it's a group.
      if (
        action.target.kind === "group" &&
        findGroupIndex(state.data, action.target.group_id) === -1
      ) {
        return state;
      }

      let nextData = state.data;

      // Remove from source.
      if (action.source.kind === "ungrouped") {
        nextData = {
          ...nextData,
          ungrouped_file_ids: nextData.ungrouped_file_ids.filter(
            (id: string) => id !== action.file_id,
          ),
        };
      } else {
        nextData = replaceGroup(
          nextData,
          action.source.group_id,
          (g: Group) => ({
            ...g,
            file_ids: g.file_ids.filter((id: string) => id !== action.file_id),
          }),
        );
      }

      // Add to target.
      if (action.target.kind === "ungrouped") {
        nextData = {
          ...nextData,
          ungrouped_file_ids: [...nextData.ungrouped_file_ids, action.file_id],
        };
      } else {
        nextData = replaceGroup(
          nextData,
          action.target.group_id,
          (g: Group) => ({
            ...g,
            file_ids: [...g.file_ids, action.file_id],
          }),
        );
      }

      return { ...state, data: nextData };
    }

    case "CREATE_MANUAL_GROUP": {
      if (state.phase !== "manual_grouping") return state;
      if (action.file_ids.length === 0) return state;
      // All source files must currently live in ungrouped.
      const ungroupedSet = new Set<string>(state.data.ungrouped_file_ids);
      for (const fileId of action.file_ids) {
        if (!ungroupedSet.has(fileId)) return state;
      }
      // Reject duplicate file ids in the action payload.
      const seen = new Set<string>();
      for (const fileId of action.file_ids) {
        if (seen.has(fileId)) return state;
        seen.add(fileId);
      }
      const removedSet = new Set<string>(action.file_ids);
      const newGroup: Group = {
        id: uuidV4(),
        file_ids: action.file_ids.slice(),
        selected_customer_id: null,
        state: "manual",
        source: "manual",
        initial_bucket: null,
      };
      return {
        ...state,
        data: {
          ...state.data,
          ungrouped_file_ids: state.data.ungrouped_file_ids.filter(
            (id: string) => !removedSet.has(id),
          ),
          groups: [...state.data.groups, newGroup],
        },
      };
    }

    case "DISSOLVE_GROUP": {
      if (state.phase !== "manual_grouping") return state;
      const index = findGroupIndex(state.data, action.group_id);
      if (index === -1) return state;
      const group = state.data.groups[index];
      // Approved groups are read-only in Phase 3.
      if (group.state === "approved") return state;
      const nextGroups = state.data.groups.slice();
      nextGroups.splice(index, 1);
      return {
        ...state,
        data: {
          ...state.data,
          groups: nextGroups,
          ungrouped_file_ids: [
            ...state.data.ungrouped_file_ids,
            ...group.file_ids,
          ],
        },
      };
    }

    case "CONTINUE_TO_FINAL": {
      if (state.phase !== "manual_grouping") return state;
      if (
        getBlockingErrorsForPhase(state.data, "manual_grouping").length > 0
      ) {
        return state;
      }
      return { ...state, phase: "final_review" };
    }

    case "BACK_TO_MANUAL": {
      // Operator-initiated back step from Phase 4 to fix something
      // (e.g. a duplicate customer they want to resolve). No data
      // mutation; just a phase change.
      if (state.phase !== "final_review") return state;
      return { ...state, phase: "manual_grouping" };
    }

    case "START_UPLOAD": {
      if (state.phase !== "final_review") return state;
      if (
        getBlockingErrorsForPhase(state.data, "final_review").length > 0
      ) {
        return state;
      }
      return { ...state, phase: "uploading" };
    }

    default: {
      // Exhaustiveness check.
      const _exhaustive: never = action;
      void _exhaustive;
      return state;
    }
  }
}
